import React, { useEffect, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { Card, Button, App as AntdApp, Divider, Typography } from 'antd';
import { TextField } from '../components/form/fields/TextField';
import { PasswordField } from '../components/form/fields/PasswordField';
import logoImage from '../assets/logo.png';
import { themeConfig } from '../layout/theme';
import { usePageTitle } from '../hooks/usePageTitle';
import { loginUser, sendTemporaryPasswordEmail } from '../api/accountsApi';
import { isAuthenticated, setAuthSession } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { appRoutes } from '../config/RoutesConfig';
import { navigateBack } from '../hooks/useBackNavigation';

const { Title, Text } = Typography;

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
});

const Login: React.FC = () => {
  const { message } = AntdApp.useApp();
  usePageTitle(appRoutes);
  const navigate = useNavigate();
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/');
    }
  }, []);

  const handleLogin = async (values: { email: string; password: string }) => {
    try {
      // Encode password as base64
      const base64Password = btoa(values.password.trim());

      // Call login API
      const res = await loginUser({...values, password:base64Password});
      const user  = res.data;
      //console.log('Login response:', res, user.mustResetPassword);
      // Redirect conditionally based on mustResetPassword
      if (user.mustResetPassword) { 
        message.success('Welcome! Your first login was successful. For security, change your password.');
        navigate('/reset-password'); // Force reset
      } else { 
        setAuthSession(user);
        message.success('Login successful');
        navigateBack(navigate); // Redirect to the dashboard or another page
      }
    } catch (error: any) {
      //console.log('Login response:', error);
      const { message:[{password="",email=""}] } = error; 
      // Extract error message from API response
      const errMessage = password || email || 'Login failed';

      // Display error message using toast
      message.error(errMessage,5);
    }
  };

  const handleForgotPassword = async ({email}: { email: string }) => {
    try {
      const res = await sendTemporaryPasswordEmail({email});
      message.success(res.data.message || 'A temporary password has been sent to your email. Please log in and reset it upon your first login.');
      setIsForgotPassword(false); // Switch back to login mode after success
    } catch (error: any) {
      //console.error('Forgot password error:', error);
      const errMessage =  error?.message || 'Failed to send temporary password email';
      message.error(errMessage);
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          {/* Logo Section */}
          <div className="text-center mb-8">
            <img
              src={logoImage}
              alt="Parking Lap Logo"
              className="w-full max-w-48 h-auto object-contain mx-auto mb-6"
            />
          </div>

          {/* Login Card */}
          <div className="relative group">
            <Card 
              className="relative bg-white border border-gray-200 shadow-lg rounded-3xl overflow-hidden"
              bodyStyle={{ padding: '2.5rem' }}
            >
              {/* Content */}
              <div className="relative z-10">
                {/* Header */}
                <div className="text-center mb-8">
                  <Title level={2} className="!text-gray-800 !mb-3 !text-3xl font-bold drop-shadow-sm">
                    {isForgotPassword ? 'Reset Password' : 'Welcome'}
                  </Title>
                  <Text className="text-gray-700 text-base drop-shadow-sm">
                    {isForgotPassword ? 'Enter your email to receive a reset link' : 'Sign in to your account to continue'}
                  </Text>
                </div>

                <Formik
                  initialValues={{ email: '', password: '' }}
                  validationSchema={isForgotPassword ? forgotPasswordSchema : loginSchema}
                  onSubmit={isForgotPassword ? handleForgotPassword : handleLogin}
                >
                  {({ isSubmitting }) => (
                    <FormikForm autoComplete="off">
                      <div className="space-y-6">
                        {/* Email Field */}
                        <div>
                          <TextField 
                            name="email" 
                            label="Email Address" 
                            placeholder="Enter your email address"
                            autoComplete="off"
                            className="!text-base"
                          />
                        </div>

                        {/* Password Field */}
                        {!isForgotPassword && (
                          <div>
                            <PasswordField 
                              name="password" 
                              label="Password" 
                              placeholder="Enter your password"
                              autoComplete="new-password"
                              className="!text-base"
                            />
                          </div>
                        )}

                        {/* Submit Button */}
                        <Button
                          type="primary"
                          htmlType="submit"
                          size="small"
                          className="w-full h-10 text-base font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                          style={{
                            backgroundColor: themeConfig.token.colorPrimary,
                            borderColor: themeConfig.token.colorPrimary,
                            color: '#fff'
                          }}
                          loading={isSubmitting}
                        >
                          {isForgotPassword ? 'Send Reset Link' : 'Sign In'}
                        </Button>

                        {/* Divider */}
                        <Divider className="!my-6 border-gray-200">
                          <Text className="text-gray-600 text-sm font-medium">or</Text>
                        </Divider>

                        {/* Toggle Mode Button */}
                        <div className="text-center">
                          <Button
                            type="link"
                            onClick={() => setIsForgotPassword(!isForgotPassword)}
                            className="text-blue-600 hover:text-blue-700 font-medium text-sm hover:underline transition-colors duration-200"
                          >
                            {isForgotPassword ? 'Back to Sign In' : 'Forgot your password?'}
                          </Button>
                        </div>
                      </div>
                    </FormikForm>
                  )}
                </Formik>
              </div>
            </Card>
          </div>

          {/* Footer */}
          <div className="text-center mt-8">
            <Text className="text-gray-600 text-sm">
              Don't have an account? 
              <Button type="link" className="!p-0 !h-auto text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors duration-200">
                Contact To Administrator
              </Button>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
